/**
 * Mapper simples de status para o stepper de Acompanhamento.
 *
 * Mantem o contrato minimo necessario para a UI funcionar:
 *   - STATUS_LABELS    : rotulo humano por chave textual
 *   - STATUS_CORES     : cor hex por chave textual
 *   - STATUS_PARA_ETAPA: mapeia a chave na etapa do stepper (1..5)
 *   - TOTAL_ETAPAS     : total fixo em 5 etapas (TCE / TCA / TCA Emp.)
 */

import { PROGRAMA_TCA } from "../utils/tipoPrograma.js";

export const STATUS_LABELS = {
  PENDENTE: "Pendente",
  AGUARDANDO_ESTUDANTE: "Aguardando preenchimento do Estudante",
  AGUARDANDO_APRENDIZ: "Aguardando preenchimento do Aprendiz",
  EM_PREENCHIMENTO: "Em preenchimento",
  AGUARDANDO_VALIDACAO_CIEEMG: "Aguardando validação do CIEEMG",
  EM_VALIDACAO: "Em validação pelo CIEEMG",
  AGUARDANDO_CONFECCAO: "Aguardando confecção do contrato",
  CONTRATO_CONFECCIONADO: "Contrato confeccionado",
  AGUARDANDO_ASSINATURAS: "Aguardando assinaturas",
  AGUARDANDO_ASSINATURA: "Aguardando assinatura",
  DISPONIBILIZADO_PARA_ASSINATURA: "Disponibilizado para assinatura",
  DISPONIBILIZADO_PARA_ASSINATURAS: "Disponibilizado para assinatura",
  FINALIZADO: "Finalizado",
  APROVADO: "Aprovado",
  CONTRATO_FINALIZADO: "Contrato finalizado",
  REPROVADO_EMPRESA: "Reprovado — voltar p/ Empresa",
  REPROVADO_ESTUDANTE: "Reprovado — voltar p/ Estudante",
  REPROVADO_DEFINITIVO: "Reprovado definitivamente",
  RECUSADO: "Recusado",
};

// Mapeia o status retornado pelo backend para a etapa equivalente no stepper
// quando o programa EXIGE a etapa extra do estudante (programa 0 ou 2).
//   1) Cadastro inicial
//   2) Preenchimento do Estudante
//   3) Validação do preenchimento
//   4) Aguardando assinatura
//   5) Contrato finalizado
export const STATUS_PARA_ETAPA_COM_ESTUDANTE = {
  AGUARDANDO_ESTUDANTE: 2,
  AGUARDANDO_APRENDIZ: 2,
  EM_PREENCHIMENTO: 2,
  AGUARDANDO_VALIDACAO_CIEEMG: 3,
  PENDENTE: 3,
  EM_VALIDACAO: 3,
  AGUARDANDO_CONFECCAO: 3,
  CONTRATO_CONFECCIONADO: 3,
  AGUARDANDO_ASSINATURAS: 4,
  AGUARDANDO_ASSINATURA: 4,
  DISPONIBILIZADO_PARA_ASSINATURA: 4,
  DISPONIBILIZADO_PARA_ASSINATURAS: 4,
  ASSINATURA: 4,
  ASSINATURAS: 4,
  FINALIZADO: 5,
  APROVADO: 5,
  CONTRATO_FINALIZADO: 5,
  REPROVADO_EMPRESA: 1,
  REPROVADO_ESTUDANTE: 2,
  REPROVADO_DEFINITIVO: 1,
  RECUSADO: 1,
};

// Mapeia o status retornado pelo backend para a etapa equivalente no stepper
// quando o programa NÃO exige etapa extra do estudante (programa 1).
//   1) Cadastro inicial
//   2) Validação do preenchimento
//   3) Aguardando assinatura
//   4) Contrato finalizado
// Mantido como mapa separado para refletir fielmente o fluxo do TCA padrão,
// onde o cadastro da empresa já contempla o preenchimento do estudante.
export const STATUS_PARA_ETAPA_SEM_ESTUDANTE = {
  AGUARDANDO_ESTUDANTE: 2,
  AGUARDANDO_APRENDIZ: 2,
  EM_PREENCHIMENTO: 2,
  AGUARDANDO_VALIDACAO_CIEEMG: 2,
  PENDENTE: 2,
  EM_VALIDACAO: 2,
  AGUARDANDO_CONFECCAO: 2,
  CONTRATO_CONFECCIONADO: 2,
  AGUARDANDO_ASSINATURAS: 3,
  AGUARDANDO_ASSINATURA: 3,
  DISPONIBILIZADO_PARA_ASSINATURA: 3,
  DISPONIBILIZADO_PARA_ASSINATURAS: 3,
  ASSINATURA: 3,
  ASSINATURAS: 3,
  FINALIZADO: 4,
  APROVADO: 4,
  CONTRATO_FINALIZADO: 4,
  REPROVADO_EMPRESA: 1,
  REPROVADO_ESTUDANTE: 1,
  REPROVADO_DEFINITIVO: 1,
  RECUSADO: 1,
};

// Alias para compatibilidade com consumidores que ainda esperam o nome
// "STATUS_PARA_ETAPA" (sem sufixo). Aponta para o mapa COM estudante, que é
// o que o Acompanhamento sempre utilizou (5 etapas). Quando a regra de
// "Preenchimento do Estudante" por programa for religada na view, basta
// passar `tipoPrograma` para `mapearEtapaPorStatus` e ela escolherá entre
// STATUS_PARA_ETAPA_COM_ESTUDANTE e STATUS_PARA_ETAPA_SEM_ESTUDANTE.
export const STATUS_PARA_ETAPA = STATUS_PARA_ETAPA_COM_ESTUDANTE;

export const STATUS_CORES = {
  PENDENTE: "#3b82f6",
  AGUARDANDO_ESTUDANTE: "#3b82f6",
  AGUARDANDO_APRENDIZ: "#3b82f6",
  EM_PREENCHIMENTO: "#3b82f6",
  AGUARDANDO_VALIDACAO_CIEEMG: "#3b82f6",
  EM_VALIDACAO: "#1e40af",
  AGUARDANDO_CONFECCAO: "#3b82f6",
  CONTRATO_CONFECCIONADO: "#1e40af",
  AGUARDANDO_ASSINATURAS: "#1e3a8a",
  AGUARDANDO_ASSINATURA: "#1e3a8a",
  DISPONIBILIZADO_PARA_ASSINATURA: "#1e40af",
  DISPONIBILIZADO_PARA_ASSINATURAS: "#1e40af",
  FINALIZADO: "#134b8b",
  APROVADO: "#1e3a8a",
  CONTRATO_FINALIZADO: "#134b8b",
  REPROVADO_EMPRESA: "#ef4444",
  REPROVADO_ESTUDANTE: "#ef4444",
  REPROVADO_DEFINITIVO: "#dc2626",
  RECUSADO: "#dc2626",
};

export const TOTAL_ETAPAS = 5;

export function obterStatusChave(status) {
  if (!status) return "";
  const s = String(status)
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove acentos
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (!s) return "";

  // Canonicaliza variações de Assinatura
  if (
    s === "AGUARDANDO_ASSINATURA" ||
    s === "AGUARDANDO_ASSINATURAS" ||
    s === "DISPONIBILIZADO_PARA_ASSINATURA" ||
    s === "DISPONIBILIZADO_PARA_ASSINATURAS" ||
    s === "ASSINATURA" ||
    s === "ASSINATURAS" ||
    s.includes("ASSINATUR")
  ) {
    return "AGUARDANDO_ASSINATURAS";
  }

  // Canonicaliza variações de Finalizado / Aprovado
  if (
    s === "FINALIZADO" ||
    s === "APROVADO" ||
    s === "CONTRATO_FINALIZADO" ||
    s.includes("FINALIZAD") ||
    s.includes("APROVAD")
  ) {
    return "FINALIZADO";
  }

  // Canonicaliza variações de Validação / CIEEMG
  if (
    s === "AGUARDANDO_VALIDACAO_CIEEMG" ||
    s === "AGUARDANDO_VALIDACAO" ||
    s === "VALIDACAO_DO_PREENCHIMENTO" ||
    s === "EM_VALIDACAO_PELO_CIEEMG" ||
    s === "EM_VALIDACAO" ||
    s.includes("VALIDACA") ||
    s.includes("VALIDACAO")
  ) {
    return "AGUARDANDO_VALIDACAO_CIEEMG";
  }

  // Canonicaliza confecção
  if (s.includes("CONFECCA") || s.includes("CONFECCAO")) {
    return "AGUARDANDO_CONFECCAO";
  }

  // Canonicaliza rejeição
  if (s.includes("REPROVADO_EMPRESA")) return "REPROVADO_EMPRESA";
  if (s.includes("REPROVADO_ESTUDANTE")) return "REPROVADO_ESTUDANTE";
  if (s.includes("REPROVAD") || s.includes("RECUSAD")) return "REPROVADO_DEFINITIVO";

  // Canonicaliza estudante / aprendiz
  if (
    s === "AGUARDANDO_ESTUDANTE" ||
    s === "AGUARDANDO_APRENDIZ" ||
    s === "EM_PREENCHIMENTO" ||
    s.includes("PREENCHIMENTO_DO_ESTUDANTE") ||
    s.includes("PREENCHIMENTO_DO_APRENDIZ")
  ) {
    return "AGUARDANDO_ESTUDANTE";
  }

  return s;
}

export function getStatusLabel(status, tipoPrograma = null) {
  const chave = obterStatusChave(status);
  if (!chave) return "Sem status";
  if (Number(tipoPrograma) === 0) {
    if (
      chave === "AGUARDANDO_ESTUDANTE" ||
      chave === "AGUARDANDO_APRENDIZ" ||
      chave === "EM_PREENCHIMENTO"
    ) {
      return "Aguardando Preenchimento do Estagiário";
    }
    if (chave === "REPROVADO_ESTUDANTE") return "Reprovado — voltar p/ Estagiário";
  }
  if (Number(tipoPrograma) === 2) {
    if (
      chave === "AGUARDANDO_ESTUDANTE" ||
      chave === "AGUARDANDO_APRENDIZ" ||
      chave === "EM_PREENCHIMENTO"
    ) {
      return "Aguardando Preenchimento do Aprendiz";
    }
    if (chave === "REPROVADO_ESTUDANTE") return "Reprovado — voltar p/ Aprendiz";
  }
  if (Number(tipoPrograma) === 1) {
    if (
      chave === "AGUARDANDO_ESTUDANTE" ||
      chave === "AGUARDANDO_APRENDIZ" ||
      chave === "EM_PREENCHIMENTO"
    ) {
      return "Aguardando validação do CIEEMG";
    }
    if (chave === "REPROVADO_ESTUDANTE") return "Reprovado — voltar p/ Empresa";
  }
  return STATUS_LABELS[chave] || chave;
}

export function getStatusCor(status) {
  const chave = obterStatusChave(status);
  return STATUS_CORES[chave] || "#94a3b8";
}

/* ============================================================
   Funções auxiliares para o stepper dinamico baseado no
   `tipo_programa` (0 = TCE / 2 = TCA Empregador → 5 etapas;
   1 = TCA → 4 etapas, sem etapa extra do estudante).
============================================================ */

/** Total padrao de etapas (TCE / TCA Empregador = 5). */
export const TOTAL_ETAPAS_PADRAO = TOTAL_ETAPAS;

/** Status finais que encerram o processo com sucesso. */
const STATUS_FINALIZADOS = new Set(["FINALIZADO", "APROVADO"]);

/** Status que indicam cancelamento/reprovacao. */
export const STATUS_REJEITADOS = new Set([
  "REPROVADO_EMPRESA",
  "REPROVADO_ESTUDANTE",
  "REPROVADO_DEFINITIVO",
  "RECUSADO",
]);

export function resolverChaveStatus(candidatos) {
  const lista = Array.isArray(candidatos) ? candidatos : [candidatos];
  const strings = lista
    .filter((c) => c !== null && c !== undefined && typeof c === "string" && c.trim())
    .map((c) => c.trim());

  // 1. Prioridade máxima: se algum campo indica Assinatura
  for (const s of strings) {
    const ch = obterStatusChave(s);
    if (ch === "AGUARDANDO_ASSINATURAS") return "AGUARDANDO_ASSINATURAS";
  }

  // 2. Prioridade: Finalizado / Aprovado
  for (const s of strings) {
    const ch = obterStatusChave(s);
    if (ch === "FINALIZADO") return "FINALIZADO";
  }

  // 3. Prioridade: Rejeitado / Recusado
  for (const s of strings) {
    const ch = obterStatusChave(s);
    if (STATUS_REJEITADOS.has(ch)) return ch;
  }

  // 4. Prioridade: Específico de Validação / Confecção / Estudante
  for (const s of strings) {
    const ch = obterStatusChave(s);
    if (
      ch === "AGUARDANDO_VALIDACAO_CIEEMG" ||
      ch === "EM_VALIDACAO" ||
      ch === "AGUARDANDO_CONFECCAO" ||
      ch === "CONTRATO_CONFECCIONADO" ||
      ch === "AGUARDANDO_ESTUDANTE"
    ) {
      return ch;
    }
  }

  // 5. Fallback geral para qualquer chave conhecida não-genérica
  for (const s of strings) {
    const ch = obterStatusChave(s);
    if (ch && ch !== "PENDENTE") return ch;
  }

  // 6. Último caso: PENDENTE
  for (const s of strings) {
    const ch = obterStatusChave(s);
    if (ch === "PENDENTE") return "PENDENTE";
  }

  return "PENDENTE";
}

/**
 * Mapeia uma chave de status (normalizada) na etapa correspondente
 * do stepper, considerando se o programa exige etapa extra do estudante.
 *
 * Usado pelo `rejeicaoMapper` para resolver a etapa de uma rejeição
 * registrada no `localStorage`.
 */
export function mapearEtapaPorStatus(statusChave, requerSupex = false, tipoPrograma = 1) {
  const chave = obterStatusChave(statusChave);
  if (!chave) return 1;

  // Em programas SEM etapa extra (TCA, tipoPrograma === 1) o total
  // de etapas é 4 (sem etapa de aprendiz/estudante).
  const ehProgramaSemEstudante =
    Number(tipoPrograma) === PROGRAMA_TCA || Number(tipoPrograma) === 1;
  const mapa = ehProgramaSemEstudante
    ? STATUS_PARA_ETAPA_SEM_ESTUDANTE
    : STATUS_PARA_ETAPA_COM_ESTUDANTE;

  let etapa = mapa[chave] || 1;

  if (ehProgramaSemEstudante) {
    if (chave === "AGUARDANDO_ESTUDANTE" || chave === "AGUARDANDO_APRENDIZ") etapa = 2;
    if (chave === "REPROVADO_ESTUDANTE") etapa = 1;
  }

  return etapa;
}

/** Total de etapas por programa. */
export function obterTotalEtapasPorPrograma(tipoPrograma, temEstudante = false) {
  if (temEstudante) return TOTAL_ETAPAS_PADRAO;
  const tp = Number(tipoPrograma);
  // 0 (TCE) e 2 (TCA Empregador) → 5 etapas (com etapa do estudante).
  // 1 (TCA sem estudante) → 4 etapas.
  if (tp === 1 || tp === PROGRAMA_TCA) return 4;
  return TOTAL_ETAPAS_PADRAO;
}

/**
 * Resolve o status, etapa, rotulo e cor de um item de acompanhamento.
 * Aplica a regra:
 *   - tipo_programa 0 ou 2 (ou qualquer com estudante/aprendiz) → 5 etapas
 *     com etapa extra "Preenchimento do Estudante/Aprendiz".
 *   - tipo_programa 1 puro (sem estudante) → fluxo de 4 etapas.
 *
 * Prioridade dos dados:
 *   1º) dados estruturados do backend (etapa_atual, label, cor, status_label)
 *      — vêm do endpoint /tca-empregador/:id/status-completo OU da rota
 *      dedicada /acompanhamentos/me (quando a API já implementa derivação);
 *   2º) inferência local via STATUS_PARA_ETAPA (comportamento antigo,
 *      preservado para fallback).
 */
export function normalizarStatusCadastro(
  fonte,
  raw,
  requerSupex,
  etapaRejeicaoHint,
  tipoPrograma,
) {
  const baseRaw = raw && Object.keys(raw || {}).length > 0 ? raw : fonte || {};
  const baseFonte = fonte || {};

  const statusStr = String(
    baseFonte?.status ||
    baseRaw?.status ||
    baseFonte?.status_label ||
    baseRaw?.status_label ||
    baseFonte?.label ||
    baseRaw?.label ||
    ""
  ).toUpperCase();

  const ehStatusAprendizOuEstudante =
    statusStr.includes("APRENDIZ") ||
    statusStr.includes("ESTUDANTE") ||
    statusStr.includes("EM_PREENCHIMENTO");

  const tpNum = Number(tipoPrograma);
  const totalRaw = Number(baseRaw?.total_etapas ?? baseFonte?.total_etapas);

  const temEtapaEstudante =
    tpNum === 0 ||
    tpNum === 2 ||
    (tpNum !== 1 && (totalRaw === 5 || ehStatusAprendizOuEstudante));

  const isTca1 = !temEtapaEstudante && (tpNum === 1 || tpNum === PROGRAMA_TCA);

  // ----- 1) Backend já enviou etapa derivada estruturada -----
  // A nova rota /tca-empregador/acompanhamentos/me devolve cada item
  // com `etapa_atual`, `label`, `cor`, `status_label`, `rejeitado`,
  // `finalizado` e `supex`. Quando esses campos estão presentes, eles
  // são a fonte da verdade — mais confiável que inferir do `status`.
  const etapaBackend =
    baseRaw?.etapa_atual ?? baseFonte?.etapa_atual ?? null;
  const totalEtapasBackend =
    baseRaw?.total_etapas ?? baseFonte?.total_etapas ?? null;
  const labelBackend =
    baseRaw?.label ?? baseFonte?.label ?? null;
  const corBackend = baseRaw?.cor ?? baseFonte?.cor ?? null;
  const statusLabelBackend =
    baseRaw?.status_label ?? baseFonte?.status_label ?? null;
  const rejeitadoBackend =
    baseRaw?.rejeitado ?? baseFonte?.rejeitado ?? null;
  const finalizadoBackend =
    baseRaw?.finalizado ?? baseFonte?.finalizado ?? null;
  const supexBackend = baseRaw?.supex ?? baseFonte?.supex ?? null;

  if (
    etapaBackend != null &&
    Number.isFinite(Number(etapaBackend))
  ) {
    const totalEtapas = isTca1
      ? 4
      : (temEtapaEstudante
          ? 5
          : (Number(totalEtapasBackend) ||
            obterTotalEtapasPorPrograma(tipoPrograma, temEtapaEstudante)));

    // Mapeia o status do backend de forma robusta
    let chaveBackend = resolverChaveStatus([
      statusLabelBackend,
      labelBackend,
      baseFonte?.status_label,
      baseFonte?.label,
      baseRaw?.status,
      baseFonte?.status,
      baseRaw?.status_canonico,
      baseFonte?.status_canonico,
    ]);

    // Se chave for genérica (PENDENTE) mas etapa_atual indicar etapa 3 (em 4 etapas) ou 4 (em 5 etapas)
    const ebNum = Number(etapaBackend);
    if (chaveBackend === "PENDENTE") {
      if (isTca1 && ebNum === 3) chaveBackend = "AGUARDANDO_ASSINATURAS";
      else if (!isTca1 && ebNum === 4) chaveBackend = "AGUARDANDO_ASSINATURAS";
      else if (isTca1 && ebNum === 4) chaveBackend = "FINALIZADO";
      else if (!isTca1 && ebNum === 5) chaveBackend = "FINALIZADO";
    }

    let labelFinal =
      chaveBackend === "AGUARDANDO_ASSINATURAS"
        ? (statusLabelBackend && /assinatur/i.test(statusLabelBackend) ? statusLabelBackend : "Aguardando assinatura")
        : chaveBackend === "FINALIZADO"
          ? (statusLabelBackend && /finalizad|aprovad/i.test(statusLabelBackend) ? statusLabelBackend : "Contrato finalizado")
          : labelBackend || statusLabelBackend || STATUS_LABELS[chaveBackend] || "Pendente";

    let corFinal = corBackend || STATUS_CORES[chaveBackend] || "#3b82f6";

    // Para formulários TCA=1 SEM aprendiz: o aprendiz não participa do fluxo.
    // Avança diretamente para validação interna.
    if (isTca1) {
      const mencionaAprendiz =
        chaveBackend === "AGUARDANDO_ESTUDANTE" ||
        chaveBackend === "AGUARDANDO_APRENDIZ" ||
        chaveBackend === "EM_PREENCHIMENTO" ||
        /aprendiz|estudante/i.test(labelFinal);

      if (mencionaAprendiz) {
        chaveBackend = "AGUARDANDO_VALIDACAO_CIEEMG";
        labelFinal = "Validação do preenchimento";
        corFinal = STATUS_CORES.AGUARDANDO_VALIDACAO_CIEEMG || "#3b82f6";
      } else if (chaveBackend === "PENDENTE") {
        labelFinal = "Validação do preenchimento";
      }
    } else if (Number(tipoPrograma) === 0) {
      // Para formulários TCE (tipo_programa 0): Estágio!
      // A etapa 2 e status de preenchimento devem SEMPRE usar "Estagiário".
      const ehEtapaPreenchimentoEstudante =
        chaveBackend === "AGUARDANDO_ESTUDANTE" ||
        chaveBackend === "AGUARDANDO_APRENDIZ" ||
        chaveBackend === "EM_PREENCHIMENTO" ||
        /aprendiz|estudante/i.test(labelFinal);

      if (ehEtapaPreenchimentoEstudante) {
        chaveBackend = "AGUARDANDO_ESTUDANTE";
        labelFinal = "Aguardando Preenchimento do Estagiário";
      } else if (chaveBackend === "REPROVADO_ESTUDANTE") {
        labelFinal = "Reprovado — voltar p/ Estagiário";
      }
    } else {
      // Para formulários com Aprendiz (TCA ou TCA Empregador):
      const ehEtapaPreenchimentoAprendiz =
        chaveBackend === "AGUARDANDO_ESTUDANTE" ||
        chaveBackend === "AGUARDANDO_APRENDIZ" ||
        chaveBackend === "EM_PREENCHIMENTO" ||
        /aprendiz|estudante/i.test(labelFinal) ||
        temEtapaEstudante;

      if (ehEtapaPreenchimentoAprendiz && (chaveBackend === "AGUARDANDO_ESTUDANTE" || chaveBackend === "AGUARDANDO_APRENDIZ" || chaveBackend === "EM_PREENCHIMENTO")) {
        chaveBackend = "AGUARDANDO_APRENDIZ";
        labelFinal = "Aguardando Preenchimento do Aprendiz";
      } else if (chaveBackend === "REPROVADO_ESTUDANTE") {
        labelFinal = "Reprovado — voltar p/ Aprendiz";
      }
    }

    const ehRejeitado =
      rejeitadoBackend === true || STATUS_REJEITADOS.has(chaveBackend);
    const ehFinalizado =
      finalizadoBackend === true || STATUS_FINALIZADOS.has(chaveBackend);

    const mapaEtapa = isTca1
      ? STATUS_PARA_ETAPA_SEM_ESTUDANTE
      : STATUS_PARA_ETAPA_COM_ESTUDANTE;
    const etapaMinimaPorStatus = mapaEtapa[chaveBackend] || 1;

    let etapaAtualFinal = Math.max(Number(etapaBackend) || 0, etapaMinimaPorStatus);
    etapaAtualFinal = Math.min(Math.max(1, etapaAtualFinal), totalEtapas);

    if (isTca1) {
      if (chaveBackend === "AGUARDANDO_ASSINATURAS") {
        etapaAtualFinal = 3;
      } else if (chaveBackend === "FINALIZADO") {
        etapaAtualFinal = 4;
      } else if (STATUS_PARA_ETAPA_SEM_ESTUDANTE[chaveBackend]) {
        etapaAtualFinal = STATUS_PARA_ETAPA_SEM_ESTUDANTE[chaveBackend];
      }

      if (chaveBackend === "AGUARDANDO_ASSINATURAS" || etapaAtualFinal === 3) {
        labelFinal = "Aguardando assinatura";
        corFinal = STATUS_CORES.AGUARDANDO_ASSINATURAS || "#1e3a8a";
      } else if (chaveBackend === "FINALIZADO" || etapaAtualFinal === 4) {
        labelFinal = "Contrato finalizado";
        corFinal = STATUS_CORES.FINALIZADO || "#134b8b";
      }
    } else {
      if (chaveBackend === "AGUARDANDO_ASSINATURAS" || etapaAtualFinal === 4) {
        labelFinal = "Aguardando assinatura";
        corFinal = STATUS_CORES.AGUARDANDO_ASSINATURAS || "#1e3a8a";
      } else if (chaveBackend === "FINALIZADO" || etapaAtualFinal === 5) {
        labelFinal = "Contrato finalizado";
        corFinal = STATUS_CORES.FINALIZADO || "#134b8b";
      }
    }

    if (ehRejeitado) {
      if (chaveBackend === "REPROVADO_ESTUDANTE") etapaAtualFinal = isTca1 ? 1 : 2;
      else etapaAtualFinal = 1;
    }
    if (typeof etapaRejeicaoHint === "number" && etapaRejeicaoHint > 0) {
      etapaAtualFinal = Math.min(etapaAtualFinal, etapaRejeicaoHint);
    }

    return {
      chave: chaveBackend || "PENDENTE",
      statusLabel: labelFinal,
      statusCor: corFinal,
      etapaAtual: etapaAtualFinal,
      totalEtapas,
      finalizado: ehFinalizado,
      rejeitado: ehRejeitado,
      requerSupex: supexBackend === true || Boolean(requerSupex),
    };
  }

  // ----- 2) Fallback: inferência local via STATUS_PARA_ETAPA -----
  let chave = resolverChaveStatus([
    fonte?.status_label,
    raw?.status_label,
    fonte?.label,
    raw?.label,
    fonte?.status,
    raw?.status,
    raw?.status_cadastro,
  ]);
  if (!chave) return null;

  const totalEtapas = isTca1
    ? 4
    : (temEtapaEstudante ? 5 : obterTotalEtapasPorPrograma(tipoPrograma, temEtapaEstudante));
  const mapaEtapa = isTca1
    ? STATUS_PARA_ETAPA_SEM_ESTUDANTE
    : STATUS_PARA_ETAPA_COM_ESTUDANTE;

  // Para TCA=1 sem estudante: o aprendiz não participa, avança para validação do CIEEMG
  if (
    isTca1 &&
    (chave === "AGUARDANDO_ESTUDANTE" ||
      chave === "AGUARDANDO_APRENDIZ" ||
      chave === "EM_PREENCHIMENTO")
  ) {
    chave = "AGUARDANDO_VALIDACAO_CIEEMG";
  }

  let etapaAtual = mapaEtapa[chave] || 1;

  // Para programas com etapa extra (0 e 2 ou com aprendiz), garante que o status
  // AGUARDANDO_ESTUDANTE/AGUARDANDO_APRENDIZ seja exibido na etapa 2.
  if (
    !isTca1 &&
    (chave === "AGUARDANDO_ESTUDANTE" || chave === "AGUARDANDO_APRENDIZ" || chave === "EM_PREENCHIMENTO")
  ) {
    etapaAtual = 2;
  }

  // Ajuste: se o item foi reprovado, manda para a etapa correta
  // (empresa = 1, estudante = 2; para TCA=1, sempre 1).
  if (STATUS_REJEITADOS.has(chave)) {
    if (chave === "REPROVADO_ESTUDANTE") etapaAtual = isTca1 ? 1 : 2;
    else etapaAtual = 1;
  }

  if (typeof etapaRejeicaoHint === "number" && etapaRejeicaoHint > 0) {
    etapaAtual = Math.min(etapaAtual, etapaRejeicaoHint);
  }

  let statusLabel = STATUS_LABELS[chave] || chave;
  if (isTca1) {
    if (
      chave === "PENDENTE" ||
      chave === "AGUARDANDO_VALIDACAO_CIEEMG" ||
      /aprendiz|estudante/i.test(statusLabel)
    ) {
      statusLabel = "Aguardando validação do CIEEMG";
    }
  } else if (Number(tipoPrograma) === 0) {
    if (
      chave === "AGUARDANDO_ESTUDANTE" ||
      chave === "AGUARDANDO_APRENDIZ" ||
      chave === "EM_PREENCHIMENTO" ||
      /aprendiz|estudante/i.test(statusLabel)
    ) {
      statusLabel = "Aguardando Preenchimento do Estagiário";
    } else if (chave === "REPROVADO_ESTUDANTE") {
      statusLabel = "Reprovado — voltar p/ Estagiário";
    }
  } else {
    if (
      chave === "AGUARDANDO_ESTUDANTE" ||
      chave === "AGUARDANDO_APRENDIZ" ||
      chave === "EM_PREENCHIMENTO" ||
      /aprendiz|estudante/i.test(statusLabel) ||
      temEtapaEstudante
    ) {
      if (chave === "AGUARDANDO_ESTUDANTE" || chave === "AGUARDANDO_APRENDIZ" || chave === "EM_PREENCHIMENTO") {
        statusLabel = "Aguardando Preenchimento do Aprendiz";
      }
    } else if (chave === "REPROVADO_ESTUDANTE") {
      statusLabel = "Reprovado — voltar p/ Aprendiz";
    }
  }

  return {
    chave,
    statusLabel,
    statusCor: STATUS_CORES[chave] || "#94a3b8",
    etapaAtual,
    totalEtapas,
    finalizado: STATUS_FINALIZADOS.has(chave),
    rejeitado: STATUS_REJEITADOS.has(chave),
    requerSupex: Boolean(requerSupex),
  };
}


